<template>
  <nav class="menu-nav no-internal-print">
    <div class="menu">
      <div v-if="isMobile"
        class="menu__mobile"
      >
        <span
          class="menu__icon material-icons"
          v-on:click="toggleCollapsed"
        >reorder</span>
        <div
          class="menu__back-drop"
          v-if="isNotCollapsed"
          v-on:click="toggleCollapsed"
        />
        <ul
          class="menu__list no-gutters list-unstyled"
          :class="colapsedAnimationClass"
        >
          <li
            v-for="(menuItem, index) in mobileItens"
            :key="index"
            @click="toggleCollapsed"
          >
            <menu-item
              :menu-item="menuItem"
              :is-mobile="isMobile"
              @open-sub-menu="openSubMenu"
            />
          </li>
          <li>
            <menu-item
              :is-logout="true"
            />
          </li>
        </ul>
      </div>
      <div v-else
        class="menu__list menu__desktop"
      >
        <ul
          v-if="isNotCollapsed"
          class="no-gutters list-unstyled"
        >
          <li
            v-for="(menuItem, index) in menuItemsTop"
            v-b-tooltip="getTooltip(menuItem)"
            :key="index"
          >
            <menu-item
              :menu-item="menuItem"
              :isMobile="isMobile"
              @open-sub-menu="openSubMenu"
              @close-sub-menu="closeSubMenu"
            />
          </li>
        </ul>
        <ul
          class="no-gutters list-unstyled"
        >
          <li
            v-for="(menuItem, index) in menuItemsBottom"
            v-b-tooltip="getTooltip(menuItem)"
            :key="index"
          >
            <menu-item
              :menu-item="menuItem"
              :isMobile="isMobile"
              @open-sub-menu="openSubMenu"
              @close-sub-menu="closeSubMenu"
            />
          </li>
          <li v-b-tooltip="getTooltip(logoutMenuItem)">
            <menu-item
              :is-logout="true"
            />
          </li>
        </ul>
      </div>
    </div>
    <sub-menu
      :menu-title="selectedMenuLabel"
      @onToggleCollapse="handleAssetSubmenu"
    />
  </nav>
</template>

<script>
import store from '@/store';
import { mapGetters } from 'vuex';
import MenuItemClass from '@/domain/data/menu/MenuItem';
import SubMenuClass from '@/domain/data/menu/SubMenu';
import SubMenu from '@/domain/components/layout/SubMenu';
import menuItemsTop from '@/domain/data/menu/menu-top.json';
import menuItemsBottom from '@/domain/data/menu/menu-bottom.json';
import helpSubMenu from '@/domain/data/menu/sub-menu-help.json';
import myAccountSubMenu from '@/domain/data/menu/sub-menu-my-account.json';
import MenuItem from '@/domain/components/layout/MenuItem';
import menuService from '@/domain/services/installation/modules/menu';
import mobileMixin from '@/services/mobileMixin';
import eventBus from '@/services/globalEventBus';
import menuEvents from '@/domain/events/menu';

export default {
  components: {
    MenuItem,
    SubMenu,
  },
  mixins: [mobileMixin],
  computed: {
    ...mapGetters([
      'subMenu',
      'assetSubMenu',
    ]),
    colapsedAnimationClass() {
      if (this.unTouched) {
        return 'menu-display__hidden';
      }
      return this.isCollapsed ? 'menu-display__colapsed' : 'menu-display__not-colapsed';
    },
    mobileItens() {
      return this.allMenuItem.filter(x => !(x.isSeparator && this.isMobile));
    },
    isNotCollapsed() {
      return !(this.isMobile && this.isCollapsed);
    },
    allMenuItem() {
      return this.createMenuItemsInstance(menuItemsTop.concat(menuItemsBottom));
    },
    assetId() {
      return this.$route.params.assetId;
    },
    logoutMenuItem() {
      return new MenuItemClass({ staticTitle: this.$t('user-menu.logout') });
    },
  },
  data: vm => ({
    currentRouteSubmenu: {
      items: [],
      routeName: '',
    },
    menuItemsTop: vm.createMenuItemsInstance(menuItemsTop),
    menuItemsBottom: vm.createMenuItemsInstance(menuItemsBottom),
    selectedMenuLabel: '',
    subMenuItems: [],
    isCollapsed: true,
    unTouched: true,
  }),
  created() {
    this.initListener();
    const { items } = this.$router?.currentRoute.params;
    this.handleMenuItems(items);
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    initListener() {
      eventBus.on(menuEvents.refreshMenuItems, this.handleMenuItems, this);
    },
    removeListener() {
      eventBus.off(menuEvents.refreshMenuItems, this.handleMenuItems, this);
    },
    handleMenuItems(menuItems) {
      if (!menuItems) {
        return;
      }
      this.subMenuItems = menuService.mapSubMenus(this.assetId, menuItems);
      this.syncSubMenuOnInit();
      this.syncSubMenuOnRouteChange();
    },
    syncSubMenuOnInit() {
      const { name: initRouteName } = this.$router?.currentRoute;
      this.openSubMenu(initRouteName);
    },
    syncSubMenuOnRouteChange() {
      this.$router.afterEach(this.syncSubMenuOnRouteChangeCallback);
    },
    syncSubMenuOnRouteChangeCallback({ name: routeNameTo }, { name: routeNameFrom }) {
      if (routeNameTo === routeNameFrom) return;
      this.openSubMenu(routeNameTo);
    },
    getSubMenuTitleByRouteName(name) {
      const [routeNamePrefix] = (name || '').split('.');
      const [menu] = this.allMenuItem.filter(({ routeName }) => routeName === routeNamePrefix);
      return menu?.label;
    },
    getTooltip(menuItem) {
      return {
        title: menuItem.title,
        trigger: 'hover',
        placement: 'right',
        boundary: 'viewport',
      };
    },
    toggleCollapsed() {
      this.unTouched = false;
      this.isCollapsed = !this.isCollapsed;
    },
    setSubMenu(subMenuItems, maintainPreviousCollapsedState = false, routeName = '') {
      if (maintainPreviousCollapsedState) {
        this.currentRouteSubmenu = {
          items: subMenuItems,
          routeName,
        };
      }

      store.dispatch('setSubMenuItems', {
        items: subMenuItems,
        maintainPreviousCollapsedState,
      });
    },
    setSubMenuRoute(routeName) {
      store.dispatch('setSubMenuRoute', routeName);
      this.selectedMenuLabel = this.getSubMenuTitleByRouteName(routeName);
    },
    closeSubMenu() {
      const { items, routeName } = this.currentRouteSubmenu;
      this.setSubMenu(items, true, routeName);
      this.setSubMenuRoute(routeName);
    },
    openSubMenu(routeName) {
      let getSubMenuItems;

      const hideSubMenu = this.hideSubMenu(routeName);

      if (hideSubMenu) {
        this.closeSubMenu();
        return;
      }

      this.setSubMenuRoute(routeName);

      switch (routeName) {
        case 'help':
        case 'help.releaseNotes':
          getSubMenuItems = Promise.resolve({ items: helpSubMenu });
          break;
        case 'my-account':
        case 'my-account.my-data':
        case 'my-account.software-license-review':
          getSubMenuItems = Promise.resolve({ items: myAccountSubMenu });
          break;
        case 'monitoring.supervision.sensors':
        case 'monitoring.engineering.module':
          getSubMenuItems = Promise.resolve({
            items: this.subMenuItems,
            maintainPreviousCollapsedState: true,
          });
          break;
        case 'monitoring.asset-information.registration-data':
        case 'monitoring.asset-information.general-data':
        case 'monitoring.asset-information.cooling-stages':
        case 'monitoring.asset-information.losses':
        case 'monitoring.asset-information.files':
          getSubMenuItems = Promise.resolve({
            items: this.subMenu.items,
            maintainPreviousCollapsedState: true,
          });
          break;
        case 'monitoring.supervision.digital-network':
        default:
          this.currentRouteSubmenu = { items: [] };
          getSubMenuItems = Promise.resolve({ items: [] });
      }

      getSubMenuItems
        .then(({ items, maintainPreviousCollapsedState }) => {
          const subMenuItems = this.createSubMenuItemsInstance(items);
          this.setSubMenu(subMenuItems, maintainPreviousCollapsedState, routeName);
          this.selectedMenuLabel = this.getSubMenuTitleByRouteName(routeName);
        });
    },
    hideSubMenu(routeName) {
      const isMonitoringRoute = (routeName || '').includes('monitoring');
      const sameRouteName = this.subMenu.routeName === routeName;
      const existItems = (this.subMenu.items || []).length !== 0;
      return !isMonitoringRoute && sameRouteName && existItems;
    },
    createMenuItemsInstance(list) {
      return this.getListAccordingToMenuPermissions(list)
        .map(item => new MenuItemClass({ ...item }));
    },
    createSubMenuItemsInstance(list) {
      return (list || []).map(item => new SubMenuClass(item));
    },
    getListAccordingToMenuPermissions(list) {
      return list.filter(menuItem => this.hasPermissionForMenu(menuItem));
    },
    hasPermissionForMenu(menuItem) {
      return !menuItem.permissions?.length
        || menuItem.permissions.some(permission => this.$hasUserPermission(permission));
    },
    handleAssetSubmenu() {
      if (this.subMenu.previousRouteName === 'monitoring.supervision.sensors') {
        this.handleMenuItems(this.assetSubMenu.items);
        return;
      }

      if (this.subMenu.previousRouteName === 'monitoring.asset-information.registration-data') {
        this.subMenu.items = this.assetSubMenu;
        this.selectedMenuLabel = this.getSubMenuTitleByRouteName(this.subMenu.previousRouteName);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
@import "src/styles/defaults/variables"
@import "src/styles/defaults/animation"

$mobileFadeMenuArea: 20vh
$mobileMenuListArea: 80vh

.menu
  position: relative
  padding: $menu-padding-y 0
  height: calc(#{$menu-height} - #{$menu-padding-y} - #{$footer-height})

.menu-display
  &__hidden
    transform: translateY(100%)

  &__colapsed
    @include slide-animation('open-menu', $start-height: 0, $end-height: 100%, $time: .8s)

  &__not-colapsed
    @include slide-animation('close-menu', $start-height: 100%, $end-height: 0px, $time: .8s)

.menu
  z-index: 1000
  +media-max-sm()
    padding: 0

  &__icon
    padding: 26px
    width: 18px
    height: 12px
    color: $color-white-100

  &__list
    position: absolute
    height: 100%
    justify-content: space-between
    background-color: $color-menu-background
    margin: 0
    display: flex
    flex-direction: column
    align-content: space-between
    overflow-y: auto

    +media-max-sm()
      align-items: flex-start
      display: grid
      gap: 10px
      background-color: $color-black-87-no-opacity
      grid-template-columns: repeat(3, 1fr)
      grid-template-rows: repeat(4, 1fr)
      height: $mobileMenuListArea
      justify-content: flex-start
      padding-top: 30px
      top: $mobileFadeMenuArea
      width: 100vw

  &__back-drop
    background: black
    position: absolute
    filter: opacity(0.5)
    top: 0
    height: 100vh
    width: 100vw

.menu-nav
  position: fixed
  top: 0
  right: $desktop-menu-width
  background-color: $color-menu-background
  bottom: 0
  left: 0
  width: $desktop-menu-width
  z-index: 1000
  height: calc(100vh - #{$footer-height})

  +media-max-sm()
    width: 100%
    height: $mobile-menu-height

  & > div
    background-color: $color-menu-background
    z-index: 1000

    +media-max-sm()
      height: $mobile-menu-height
      width: 100vw

</style>
